import React, { useState } from "react";

import { faSearch, faCircleNotch, faUserSlash } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Paper, TextField, List, InputAdornment, Typography, Collapse, Menu } from "@material-ui/core";

import { useObservableCallback, pluckCurrentTargetValue, useObservable, useObservableState } from "observable-hooks";
import { useTranslation } from "react-i18next";
import { of } from "rxjs";
import { switchMap, map, filter, debounceTime, catchError } from "rxjs/operators";

import type { IPartnerUserRolesAndLabels, IResponseWithLoading, ISiteSlim, IPartnerUserSlim } from "App/Modules/Models";
import type { IUsersRepository } from "App/Modules/Repositories";
import { extendWithLoadingAndFail } from "App/Modules/Services";
import { MenuItemWithIcon, ListItemWithIcon, ValidationError } from "App/ReactComponents/Shared";
import { analysisPartiallyEnabled } from "../../../Img/Icons";

import { UserByCodeListItem } from "./UserByCodeListItem";

import "./_icon-button.scss";
import "./_user-finder.scss";

type InjectedProps = {
    irUsersRepository: IUsersRepository;
    onScheduleExceptionsDialogOpen: (selectedUser: any, selectedSite: any) => void;
};

export const FindUserByCode = (props: InjectedProps) => {
    const { t } = useTranslation();

    const [onSearchChange, searchText$] = useObservableCallback<
        string,
        React.FormEvent<HTMLInputElement | HTMLTextAreaElement>
    >((e) =>
        e.pipe(
            pluckCurrentTargetValue,
            map((text) => text?.replace(/[^a-z0-9]/gi, "")),
            map((text) => text?.toUpperCase()),
        ),
    );

    const users$ = useObservable(() =>
        searchText$
            .pipe(
                filter((code) => code.length >= 4),
                switchMap((code) => of(code).pipe(debounceTime(250))),
            )
            .pipe(
                switchMap((code) =>
                    extendWithLoadingAndFail<IPartnerUserRolesAndLabels[], { code: string }>(
                        props.irUsersRepository.getUsers(code).pipe(
                            map((response) => ({ code, ...response })),
                            catchError((response) =>
                                of<IResponseWithLoading<IPartnerUserRolesAndLabels[]> & { code: string }>(response),
                            ),
                        ),
                    ),
                ),
            ),
    );

    const searchText = useObservableState(searchText$, "");
    const users = useObservableState(users$);

    const [siteMenuSite, setSiteMenuSite] = useState<ISiteSlim>(null);
    const [selectedUser, setSelectedUser] = useState<IPartnerUserSlim>(null);

    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState<Element>(null);

    const handleMenuClicked = (site: ISiteSlim, user: IPartnerUserSlim, event: React.SyntheticEvent<any>) => {
        event.stopPropagation();
        setSiteMenuSite(site);
        setSelectedUser(user);
        setMenuAnchorEl(event.currentTarget);
        setIsMenuOpen(true);
    };

    const handleScheduleExceptionClick = () => {
        props.onScheduleExceptionsDialogOpen(selectedUser, siteMenuSite);
        setIsMenuOpen(false);
        setSelectedUser(null);
        setSiteMenuSite(null);
        setMenuAnchorEl(null);
    };

    return (
        <div className="user-finder">
            <Paper>
                <div className="user-finder__content">
                    <Typography gutterBottom variant="h3" className="user-finder__title">
                        {t("Gui.Manage.ShowUserAuthorizations")}
                    </Typography>
                    <TextField
                        className="user-finder__search-field"
                        placeholder={t("Gui.Manage.SearchForAuthCode")}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <FontAwesomeIcon icon={faSearch} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    {users?.isLoading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : <></>}
                                </InputAdornment>
                            ),
                        }}
                        onChange={onSearchChange}
                        value={searchText}
                        fullWidth
                    />
                    <Collapse in={searchText === users?.code} timeout="auto">
                        {users?.isSuccessful && users?.data?.length === 0 && (
                            <List className="user-finder__list">
                                <ListItemWithIcon
                                    icon={faUserSlash}
                                    key="user"
                                    disabled
                                    primary={t("Entities.User.NoUsersFoundWithCodeFormat", { code: users?.code })}
                                />
                            </List>
                        )}
                        <ul className="entity-list">
                            {users?.data?.map((user) => (
                                <UserByCodeListItem user={user} key={user.id} handleMenuClicked={handleMenuClicked} />
                            ))}
                        </ul>
                    </Collapse>
                </div>
                <ValidationError genericErrorMessage="Error.GenericError" response={users} />
            </Paper>

            <Menu
                open={isMenuOpen}
                onClose={() => {
                    setSiteMenuSite(null);
                    setSelectedUser(null);
                    setMenuAnchorEl(null);
                    setIsMenuOpen(false);
                }}
                anchorEl={menuAnchorEl}
            >
                <MenuItemWithIcon onClick={() => handleScheduleExceptionClick()} icon={analysisPartiallyEnabled}>
                    {t("Entities.ScheduleException.Manage")}
                </MenuItemWithIcon>
            </Menu>
        </div>
    );
};
